import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { COMPANY } from "../mock";

const SECTIONS = [
  {
    id: "1",
    title: "1. About these terms",
    body: (
      <>
        <p>
          These Terms &amp; Conditions ("Terms") govern all design, development,
          social-media management and related digital services ("Services")
          provided by {COMPANY.name} ("we", "us", "our") to you, the client
          ("you", "your").
        </p>
        <p>
          By engaging our Services, paying a deposit or signing a written
          quotation, you confirm that you have read, understood and agreed to
          be bound by these Terms.
        </p>
      </>
    ),
  },
  {
    id: "2",
    title: "2. Services we provide",
    body: (
      <>
        <p>
          We provide professional digital services which may include, but are
          not limited to:
        </p>
        <ul>
          <li>Website design, build, hosting and maintenance</li>
          <li>E-commerce design and development</li>
          <li>Custom software, web applications and AI-powered tools</li>
          <li>Branding, graphic design and digital content production</li>
          <li>
            Social media consultancy, content creation, scheduling and paid
            campaign management
          </li>
          <li>Search engine optimisation (SEO) and ongoing performance reporting</li>
        </ul>
        <p>
          The specific Services to be provided for your project will be set
          out in a written quotation, proposal or service agreement.
        </p>
      </>
    ),
  },
  {
    id: "3",
    title: "3. Quotations and project scope",
    body: (
      <>
        <p>
          All quotations are valid for 30 days from the date of issue. The
          quotation defines the agreed project scope. Any work requested
          outside the agreed scope will be quoted separately as a change
          request and may extend agreed timelines.
        </p>
        <p>
          Estimated delivery dates are provided in good faith but are not
          guaranteed where delays are caused by the client (for example,
          delays in providing content, feedback or approvals).
        </p>
      </>
    ),
  },
  {
    id: "4",
    title: "4. Deposits and payment terms",
    body: (
      <>
        <p>
          <strong>Deposits are non-refundable on all projects.</strong> A
          deposit (typically 50% of the project total, unless otherwise
          agreed in writing) is required before any work commences. This
          deposit covers initial discovery, planning, design time and
          reserved studio capacity.
        </p>
        <p>
          The remaining balance is invoiced on project completion and is
          payable within 14 days. We reserve the right to withhold delivery
          of final files, transfer of accounts or go-live of any website
          until full payment has been received.
        </p>
        <p>
          Late payments may incur interest at 4% above the Bank of England
          base rate, in line with the Late Payment of Commercial Debts
          (Interest) Act 1998.
        </p>
      </>
    ),
  },
  {
    id: "5",
    title: "5. Rolling services — no long-term contracts",
    body: (
      <>
        <p>
          All ongoing services (including social media management, website
          care plans, SEO retainers and content production) are offered on a{" "}
          <strong>4-week rolling basis</strong>. We do not lock our clients
          into long-term contracts.
        </p>
        <p>
          To cancel or pause an ongoing service, simply give us written
          notice (email is fine) before the start of your next 4-week cycle.
          Any partial cycles already in progress remain payable in full.
        </p>
        <p>
          Recurring fees are payable in advance at the start of each 4-week
          cycle. Failure to pay may result in immediate suspension of the
          Services.
        </p>
      </>
    ),
  },
  {
    id: "6",
    title: "6. Client responsibilities",
    body: (
      <>
        <p>You agree to:</p>
        <ul>
          <li>
            Provide accurate, complete and timely content, brand assets,
            access credentials and feedback as reasonably required.
          </li>
          <li>
            Respond to requests for feedback or approval within 5 working
            days. Projects that stall for more than 30 days due to a lack of
            client response may be paused and re-quoted on resumption.
          </li>
          <li>
            Ensure that any materials you supply (text, images, logos,
            videos) do not infringe third-party rights and that you have
            permission to use them.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "7",
    title: "7. Revisions and approvals",
    body: (
      <>
        <p>
          Each project includes a reasonable number of design revisions, as
          specified in your quotation. Additional revisions beyond the agreed
          number are charged at our standard hourly rate.
        </p>
        <p>
          Once you have signed off on a stage (e.g. design, copy or
          development), subsequent changes to that stage will be treated as
          additional work.
        </p>
      </>
    ),
  },
  {
    id: "8",
    title: "8. Intellectual property",
    body: (
      <>
        <p>
          On full payment of all sums due, ownership of the final deliverables
          (such as the live website, designs, written content and code we
          have produced specifically for you) transfers to you.
        </p>
        <p>
          We retain ownership of any pre-existing tools, frameworks,
          libraries, templates and underlying systems used to produce the
          deliverables. We also reserve the right to display completed work
          in our portfolio and case studies, unless agreed otherwise in
          writing.
        </p>
      </>
    ),
  },
  {
    id: "9",
    title: "9. Third-party services",
    body: (
      <>
        <p>
          Many projects rely on third-party platforms (such as hosting
          providers, payment processors, CMS platforms, social media
          networks, email services and AI APIs). You are responsible for the
          ongoing cost of any third-party subscriptions, and we accept no
          liability for changes, outages or pricing decisions made by those
          providers.
        </p>
      </>
    ),
  },
  {
    id: "10",
    title: "10. Cancellation and refunds",
    body: (
      <>
        <p>
          You may cancel a project at any time by giving written notice. If
          you cancel before project completion:
        </p>
        <ul>
          <li>The deposit is non-refundable.</li>
          <li>
            You will be invoiced for any additional work completed up to the
            date of cancellation, beyond the value of the deposit.
          </li>
        </ul>
        <p>
          We reserve the right to terminate an engagement if a client
          behaves abusively, repeatedly misses payments or asks us to do
          anything illegal or unethical. In such cases no refund of work
          already completed will be due.
        </p>
      </>
    ),
  },
  {
    id: "11",
    title: "11. Confidentiality",
    body: (
      <>
        <p>
          We treat all client information as confidential and will not share
          it with third parties without your consent, except where required
          by law or where reasonably necessary to deliver the Services
          (e.g. with hosting or payment providers).
        </p>
      </>
    ),
  },
  {
    id: "12",
    title: "12. Liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, our total liability arising
          from or in connection with the Services shall not exceed the total
          fees paid by you in respect of the specific project giving rise to
          the claim.
        </p>
        <p>
          We are not liable for any indirect, consequential or economic loss
          (including loss of profits, revenue, anticipated savings, goodwill
          or data) howsoever arising.
        </p>
        <p>
          Nothing in these Terms excludes or limits our liability for death
          or personal injury caused by negligence, for fraud, or for any
          other liability that cannot be excluded by law.
        </p>
      </>
    ),
  },
  {
    id: "13",
    title: "13. Governing law",
    body: (
      <>
        <p>
          These Terms are governed by the laws of Scotland. Any disputes
          shall be subject to the exclusive jurisdiction of the Scottish
          courts.
        </p>
      </>
    ),
  },
  {
    id: "14",
    title: "14. Changes to these terms",
    body: (
      <>
        <p>
          We may update these Terms from time to time. The current version
          will always be available on this page. Material changes will be
          notified to active clients by email.
        </p>
      </>
    ),
  },
  {
    id: "15",
    title: "15. Contact",
    body: (
      <>
        <p>
          Questions about these Terms? Get in touch and we&rsquo;ll be happy
          to help.
        </p>
        <ul>
          <li>
            Email:{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-sky-700 hover:underline">
              {COMPANY.email}
            </a>
          </li>
          <li>
            Phone:{" "}
            <a href={`tel:${COMPANY.phoneRaw}`} className="text-sky-700 hover:underline">
              {COMPANY.phone}
            </a>
          </li>
          <li>Address: {COMPANY.address}</li>
        </ul>
      </>
    ),
  },
];

export default function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "29 July 2025";

  return (
    <div className="relative bg-white text-slate-900">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <div className="mt-8">
            <span className="section-eyebrow">Legal</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
              Terms &amp; Conditions
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              The terms below set out the basis on which {COMPANY.name}{" "}
              provides design, development and digital marketing services to
              its clients. Please read them carefully.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              Last updated: {lastUpdated}
            </p>
          </div>

          <div className="mt-12 space-y-10">
            {SECTIONS.map((s) => (
              <section key={s.id} id={`section-${s.id}`}>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">
                  {s.title}
                </h2>
                <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-slate-700 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:my-2 [&_a]:underline">
                  {s.body}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-16 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
            By engaging {COMPANY.name} you confirm you have read and agree to
            be bound by these Terms &amp; Conditions.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
